/**
 * Find + merge duplicate companies.
 *
 * GET /companies/duplicates returns clusters of likely-duplicate rows
 * (rapidfuzz WRatio ≥ 85 by default). The operator picks one row as
 * the canonical / primary and merges the others into it via
 * POST /companies/{primary_id}/merge.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Merge,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

interface DupRow {
  id: number;
  name: string;
  website?: string | null;
  industry?: string | null;
  description?: string | null;
  contact_email?: string | null;
  funding_stage?: string | null;
  group_name?: string | null;
  research_completion_pct?: number;
  created_at?: string | null;
}

interface DupCluster {
  representative_name: string;
  confidence: number;
  size: number;
  rows: DupRow[];
}

const CompanyDuplicatesPage: React.FC = () => {
  const [clusters, setClusters] = useState<DupCluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(85);
  // primaryId per cluster (keyed by cluster index) — defaults to the
  // top-ranked row the backend sorted to position 0.
  const [primaryByCluster, setPrimaryByCluster] = useState<Record<number, number>>({});
  // Which (cluster, loser) pair is currently merging.
  const [merging, setMerging] = useState<string | null>(null);

  const load = async (score: number = minScore) => {
    setLoading(true);
    try {
      const r = await api.get(`/companies/duplicates`, {
        params: { min_score: score },
      });
      const data: DupCluster[] = r.data?.clusters || [];
      setClusters(data);
      const initial: Record<number, number> = {};
      data.forEach((c, idx) => {
        if (c.rows.length > 0) initial[idx] = c.rows[0].id;
      });
      setPrimaryByCluster(initial);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail || 'Failed to load duplicate clusters'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(minScore);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMerge = async (clusterIdx: number, loserId: number) => {
    const primaryId = primaryByCluster[clusterIdx];
    if (!primaryId) {
      toast.error('Pick a canonical row first');
      return;
    }
    if (primaryId === loserId) {
      toast.error('Cannot merge a row into itself');
      return;
    }
    const cluster = clusters[clusterIdx];
    const primary = cluster.rows.find((r) => r.id === primaryId);
    const loser = cluster.rows.find((r) => r.id === loserId);
    if (!primary || !loser) return;

    const confirmed = window.confirm(
      `Merge "${loser.name}" (#${loser.id}) into "${primary.name}" (#${primary.id})?\n\n` +
        `All analyses, deal rooms, documents, and matches on #${loser.id} ` +
        `will be re-pointed to #${primary.id}. The duplicate row will then ` +
        `be deleted. This cannot be undone.`
    );
    if (!confirmed) return;

    const key = `${clusterIdx}:${loserId}`;
    setMerging(key);
    try {
      const r = await api.post(
        `/companies/${primaryId}/merge`,
        null,
        { params: { duplicate_id: loserId } }
      );
      const moved = r.data?.rows_moved || {};
      const movedSummary = Object.entries(moved)
        .map(([t, n]) => `${t}: ${n}`)
        .join(', ');
      toast.success(
        `Merged ${loser.name} → ${primary.name}` +
          (movedSummary ? ` (moved ${movedSummary})` : '')
      );
      // Refresh — the cluster may shrink to 1 row and disappear.
      await load(minScore);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div>
          <Link
            to="/companies"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to companies
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            Duplicate companies
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Companies whose names match each other fuzzily (rapidfuzz WRatio ≥{' '}
            {minScore}). Pick the canonical record in each cluster and merge
            the others into it — child rows (analyses, deal rooms,
            documents, matches) are re-pointed, blank fields on the primary
            are patched from the loser, and the duplicate row is deleted.
          </p>
        </div>

        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-4">
          <label className="text-sm text-gray-700 flex items-center gap-2">
            Match threshold (0–100):
            <input
              type="number"
              min={70}
              max={100}
              value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value) || 85)}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
            />
            <span className="text-xs text-gray-500">
              (lower = more matches, more false positives)
            </span>
          </label>
          <button
            onClick={() => load(minScore)}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1.5" />
            )}
            Rescan
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Scanning…
          </div>
        ) : clusters.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              No duplicate clusters above the threshold.
            </h2>
            <p className="text-sm text-gray-600">
              Try lowering the threshold to surface weaker matches.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {clusters.map((cluster, idx) => {
              const primaryId = primaryByCluster[idx];
              return (
                <section
                  key={`${cluster.representative_name}-${idx}`}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                >
                  <header className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                    <div>
                      <h2 className="font-semibold text-gray-900">
                        {cluster.representative_name}
                      </h2>
                      <p className="text-xs text-gray-500">
                        {cluster.size} rows · min match score{' '}
                        {cluster.confidence}/100
                      </p>
                    </div>
                    {cluster.confidence < 90 && (
                      <span className="inline-flex items-center text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                        <ShieldAlert className="w-3 h-3 mr-1" />
                        Weaker match — verify before merging
                      </span>
                    )}
                  </header>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-gray-500 bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left w-10">Canon.</th>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Website</th>
                        <th className="px-4 py-2 text-left">Industry</th>
                        <th className="px-4 py-2 text-left">Stage</th>
                        <th className="px-4 py-2 text-left">Group</th>
                        <th className="px-4 py-2 text-left">Research%</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cluster.rows.map((row) => {
                        const isPrimary = primaryId === row.id;
                        const key = `${idx}:${row.id}`;
                        return (
                          <tr
                            key={row.id}
                            className={
                              isPrimary ? 'bg-blue-50' : 'border-t border-gray-100'
                            }
                          >
                            <td className="px-4 py-2">
                              <input
                                type="radio"
                                name={`primary-${idx}`}
                                checked={isPrimary}
                                onChange={() =>
                                  setPrimaryByCluster((prev) => ({
                                    ...prev,
                                    [idx]: row.id,
                                  }))
                                }
                              />
                            </td>
                            <td className="px-4 py-2 font-medium text-gray-900">
                              <Link
                                to={`/companies/${row.id}`}
                                className="hover:underline"
                              >
                                {row.name}
                              </Link>
                              <span className="text-xs text-gray-400 ml-1">
                                #{row.id}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.website ? (
                                <a
                                  href={
                                    row.website.startsWith('http')
                                      ? row.website
                                      : `https://${row.website}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {row.website.replace(/^https?:\/\//, '')}
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.industry || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.funding_stage || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.group_name || '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-700">
                              {row.research_completion_pct ?? 0}%
                            </td>
                            <td className="px-4 py-2 text-right">
                              {isPrimary ? (
                                <span className="text-xs text-blue-700 font-medium">
                                  Keep
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleMerge(idx, row.id)}
                                  disabled={merging === key}
                                  className="inline-flex items-center px-2 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
                                  title={`Merge this row into #${primaryId}`}
                                >
                                  {merging === key ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Merge className="w-3 h-3 mr-1" />
                                  )}
                                  Merge into canon.
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CompanyDuplicatesPage;
